import { useEffect, useRef, useState } from "react";

// Busca dinâmica (digita → lista → escolhe). Usado em cliente/produto da Nova PCF.
interface Props<T> {
  placeholder?: string;
  valorInicial?: string;
  buscar: (q: string) => Promise<T[]>;
  rotulo: (t: T) => string;
  onEscolher: (t: T) => void;
  dataCampo?: string;
  /** mínimo de caracteres para buscar (padrão 1); 0 = busca também com o campo vazio, ao entrar nele */
  minimo?: number;
  autoFocus?: boolean;
}

export function Typeahead<T>({ placeholder, valorInicial = "", buscar, rotulo, onEscolher, dataCampo, minimo = 1, autoFocus }: Props<T>) {
  const [texto, setTexto] = useState(valorInicial);
  const [itens, setItens] = useState<T[]>([]);
  const [aberto, setAberto] = useState(false);
  const [idx, setIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const tmr = useRef<number | null>(null);

  useEffect(() => setTexto(valorInicial), [valorInicial]);

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  function onInput(v: string) {
    setTexto(v);
    setAberto(true);
    setIdx(-1);
    if (tmr.current) window.clearTimeout(tmr.current);
    if (v.trim().length < minimo) {
      setItens([]);
      return;
    }
    tmr.current = window.setTimeout(async () => {
      try {
        setItens(await buscar(v.trim()));
      } catch {
        setItens([]);
      }
    }, 250);
  }

  function escolher(t: T) {
    setTexto(rotulo(t));
    setAberto(false);
    setItens([]);
    onEscolher(t);
  }

  function onKey(e: React.KeyboardEvent) {
    if (!aberto || itens.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, itens.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const t = itens[idx];
      if (t) {
        e.preventDefault();
        escolher(t);
      }
    } else if (e.key === "Escape") {
      setAberto(false);
    }
  }

  return (
    <div className="ta" ref={ref}>
      <input
        className="ta-input"
        placeholder={placeholder}
        value={texto}
        data-campo={dataCampo}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={onKey}
        autoFocus={autoFocus}
        onFocus={() => (itens.length > 0 ? setAberto(true) : minimo === 0 && !texto.trim() && onInput(""))}
      />
      {aberto && itens.length > 0 && (
        <ul className="ta-lista">
          {itens.map((t, i) => (
            <li
              key={i}
              className={i === idx ? "ta-sel" : ""}
              onMouseDown={(e) => {
                e.preventDefault();
                escolher(t);
              }}
            >
              {rotulo(t)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
